import { useCallback, useEffect, useRef, useState } from 'react';
import { useStoreActions } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error/error';

interface UseRequestOptions<TData> {
  enabled?: boolean;
  initialLoading?: boolean;
  showErrorEnabled?: boolean;
  onSuccess?: (data: TData) => void;
  onError?: (error: string) => void;
  dependencies?: readonly unknown[];
}

interface UseRequestResult<TData> {
  isLoading: boolean;
  refetch: () => Promise<void>;
  data: TData | null;
}

interface UseApiRequestContext {
  signal: AbortSignal;
}

function useApiRequest<TData>(
  queryFnOrArray:
    | ((context: UseApiRequestContext) => Promise<TData>)
    | readonly ((context: UseApiRequestContext) => Promise<any>)[]
    | (() => Promise<TData>)
    | readonly (() => Promise<any>)[],
  options: UseRequestOptions<TData> = { showErrorEnabled: true },
): UseRequestResult<TData> {
  const { enabled = true, initialLoading = false, dependencies = [] } = options;
  const [isLoading, setIsLoading] = useState(initialLoading && enabled);
  const [data, setData] = useState<TData | null>(null);

  const { showError } = useStoreActions(actions => actions.snackbar);

  const abortControllerRef = useRef<AbortController>();

  const queryFnRef = useRef(queryFnOrArray);
  const optionsRef = useRef(options);

  queryFnRef.current = queryFnOrArray;
  optionsRef.current = options;

  const fetchData = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoading(true);

    const context: UseApiRequestContext = { signal: controller.signal };

    try {
      let result: TData;

      if (Array.isArray(queryFnRef.current)) {
        const results = await Promise.all(
          queryFnRef.current.map(fn =>
            (fn as (ctx: UseApiRequestContext) => Promise<any>)(context),
          ),
        );
        result = results as TData;
        setData(result);
      } else {
        const fn = queryFnRef.current as (ctx: UseApiRequestContext) => Promise<TData>;
        result = await fn(context);
        setData(result);
      }

      if (controller.signal.aborted) {
        return;
      }

      optionsRef.current.onSuccess?.(result);
    } catch (err) {
      if (controller.signal.aborted) {
        return;
      }

      const errorMessage = extractErrorMessage(err);
      if (optionsRef.current.showErrorEnabled !== false) {
        showError(errorMessage);
      }
      optionsRef.current.onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refetch = useCallback(async () => fetchData(), [fetchData]);

  useEffect(() => {
    if (enabled) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...dependencies]);

  useEffect(
    () => () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    },
    [],
  );

  return {
    isLoading,
    refetch,
    data,
  };
}

export default useApiRequest;
